import React, { useState, useRef } from 'react'
import Grid from '@material-ui/core/Grid'
import { Field } from 'react-final-form'
import { CrudForm, useEntityManager } from 'elentari'
import bloqueioManualRepository from './bloqueioManualRepository'
import * as yup from 'yup'
import yupValidation from '../../lib/yupValidation'
import { parseCpf, parseCnpj, parsePlaca } from '../../utils/Parse'
import MiniStepper from '../../components/MiniStepper'
import HistoricoBloqueioDesbloqueio from '../../components/HistoricoBloqueioDesbloqueio'
import { AnexosCard } from './Anexos'
import { Card, makeStyles, Step, StepButton, Stepper } from '@material-ui/core'
import {
  CustomTextField,
  CustomSelect,
  CustomMaskedField
} from '../../components/CustomDisabledFields'
import { BloqueioManualActionsColors } from './bloqueioManualActions'
import ComplexDialog from '../../components/ComplexDialog'
import Typography from '@material-ui/core/Typography'
import { getAlphanumericDigits } from '../../utils/Utils'
import { cnpjRegex, cpfRegex } from '../../utils/regex'
import UsuarioAutocomplete from '../Usuarios/UsuarioAutocomplete'
import { useAbility } from '../Usuarios'
import { AutocompleteCodigosMotivos } from '../../components'
import { codigosMotivosOptions } from '../../components/AutocompleteCodigosMotivos'

const bloqueioManualSchema = yup.object().shape({
  tipobloqueio: yup.number().required('Obrigatório'),
  cpfcnpj: yup
    .string()
    .when('tipobloqueio', {
      is: 1,
      then: yup.string().required('Obrigatório')
    })
    .when('tipobloqueio', {
      is: 2,
      then: yup.string().required('Obrigatório')
    }),
  placa: yup.string().when('tipobloqueio', {
    is: 3,
    then: yup.string().required('Obrigatório')
  }),
  codigosMotivos: yup.array().required('Obrigatório')
})

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(2)
  },
  mainStepper: {
    overflowX: 'scroll',
    marginBottom: theme.spacing(2)
  },
  desbloquear: {
    fontSize: '0.695em',
    height: '100%',
    backgroundColor: BloqueioManualActionsColors.DESBLOQUEIO,
    '&:hover': {
      color: 'white',
      backgroundColor: BloqueioManualActionsColors.DESBLOQUEIO_DARK
    }
  },
  bloquear: {
    fontSize: '0.695em',
    height: '100%',
    backgroundColor: BloqueioManualActionsColors.BLOQUEIO,
    '&:hover': {
      color: 'white',
      backgroundColor: BloqueioManualActionsColors.BLOQUEIO_DARK
    }
  }
}))

export const validateBloqueioManual = yupValidation(bloqueioManualSchema)

function getSteps() {
  return ['Bloqueio Manual', 'Anexos', 'Histórico']
}

const BloqueioManualForm = props => {
  const steps = getSteps()
  const historicoStepIndex = steps.length - 1
  const [activeStep, setActiveStep] = useState(0)
  const [historicoVisitado, setHistoricoVisitado] = useState(false)
  const [historicoRefreshKey, setHistoricoRefreshKey] = useState(0)
  const [ability] = useAbility()
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    callback: null,
    id: 0,
    mensagem: '',
    isPendente: false
  })
  const classes = useStyles()
  const elementRef = useRef()
  const entityManager = useEntityManager({
    repository: bloqueioManualRepository,
    initialData: {},
  })

  const initialCodigosMotivos = entityManager.item?.codigosMotivos
  const [codigosMotivos, setCodigosMotivos] = useState([])
  const [showErrorCodigosMotivos, setShowErrorCodigosMotivos] = useState(false)
  const initialNotasInternas = entityManager.item?.notasinternas
  const [notasInternas, setNotasInternas] = useState(initialNotasInternas)

  const scrollTop = () => {
    const stepElement = document.getElementById('content')
    setTimeout(() => {
      stepElement && stepElement.scrollIntoView()
    }, 200)
  }

  const handleStep = step => async () => {
    setActiveStep(step)
    if (step === historicoStepIndex) {
      setHistoricoVisitado(true)
    }
    scrollTop()
  }

  const onChangeTipoBloqueio = form => {
    form.change('cpfcnpj', '')
    form.change('placa', '')
  }

  const cannotUpdate =
    ability.cannot('update', 'bloqueio-manual-kmm') ||
    (!entityManager.isNew && !entityManager.item.dataDesbloqueio)

  const handleActionsClick = async (id, callback, operacao) => {
    setConfirmationDialog({
      open: true,
      callback,
      id,
      mensagem: `A seguinte operação será executada: ${operacao}`,
      isBloqueio: operacao !== 'Remover Bloqueio'
    })
  }

  const closeDialog = () => {
    setConfirmationDialog({
      mensagem: '',
      isPendente: false,
      open: false,
      callback: null,
      id: 0
    })
  }

  const handleConfirmationDialog = async data => {
    const { id, callback } = confirmationDialog
    data.notasinternas = notasInternas ?? initialNotasInternas

    if (codigosMotivos?.length > 0) {
      data.codigosMotivos = codigosMotivos
    }

    if (callback) {
      await callback(id, data)
      entityManager.getItem()
      // Força o componente de Histórico a remontar e rebuscar, já que ele
      // mantém cache interno e não tem outra forma de saber que o registro
      // acabou de ser bloqueado/desbloqueado.
      setHistoricoRefreshKey(key => key + 1)
    }
  }

  const customActionsFactory = item => {
    const desbloquear = {
      can: ['admin', 'analista_op', 'analista_op_2'],
      label: 'Desbloquear',
      props: {
        color: 'inherit',
        className: classes.desbloquear,
        variant: 'contained'
      },
      onClick: () => {
        handleActionsClick(
          item.id,
          bloqueioManualRepository.desbloquear,
          'Remover Bloqueio'
        )
      }
    }

    const onClickBloquear = () => {
      codigosMotivos?.length > 0
        ? handleActionsClick(
            item.id,
            bloqueioManualRepository.bloquear,
            'Adicionar Bloqueio'
          )
        : setShowErrorCodigosMotivos(true)
    }

    const bloquear = {
      can: ['admin', 'analista_op', 'analista_op_2'],
      label: 'Bloquear',
      props: {
        color: 'inherit',
        className: classes.bloquear,
        variant: 'contained'
      },
      onClick: onClickBloquear
    }

    const actions = []
    if (
      !item.dataDesbloqueio &&
      !entityManager.isNew &&
      ability.can('update', 'bloqueio-manual-kmm')
    ) {
      actions.push(desbloquear)
    }
    if (
      item.dataDesbloqueio &&
      !entityManager.isNew &&
      ability.can('update', 'bloqueio-manual-kmm')
    ) {
      actions.push(bloquear)
    }
    return actions
  }

  const customActions = customActionsFactory(entityManager.item)

  const handleSubmit = async data => {
    if (!cannotUpdate) {
      if (data.cpfcnpj) {
        data.cpfcnpj = getAlphanumericDigits(data.cpfcnpj)
      }
      if (data.placa) {
        data.placa = getAlphanumericDigits(data.placa)
      }
      const response = await entityManager.onSubmit(data)
      if (response.ok) {
        await entityManager.getItem()
      }
      alert('Já existe um registro com o cpf, cnpj ou placa informados')
      return response
    }
  }

  return (
    <>
      <MiniStepper
        elementRef={elementRef}
        steps={steps}
        activeStep={activeStep}
        handleStep={handleStep}
      />
      <Card className={classes.mainStepper}>
        <Stepper activeStep={activeStep} nonLinear>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton onClick={handleStep(index)}>{label}</StepButton>
            </Step>
          ))}
        </Stepper>
      </Card>
      {activeStep === 0 && (
        <CrudForm
          {...props}
          {...entityManager}
          withPaper
          hideSubmit={cannotUpdate}
          disableSubmit={cannotUpdate}
          customActions={customActions}
          validate={validateBloqueioManual}
          onSubmit={handleSubmit}
        >
          {({ values, form }) => {
            return (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    fullWidth
                    name="tipobloqueio"
                    label="Tipo de Bloqueio"
                    hideEmpty
                    component={CustomSelect}
                    onChange={() => onChangeTipoBloqueio(form)}
                    disabled={cannotUpdate || !entityManager.isNew}
                    options={[
                      { label: 'Proprietário', value: 1 },
                      { label: 'Motorista', value: 2 },
                      { label: 'Veículo', value: 3 }
                    ]}
                  />
                </Grid>
                {(values.tipobloqueio === 1 || values.tipobloqueio === 2) && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      name="cpfcnpj"
                      label="CPF/CNPJ"
                      parse={values.tipobloqueio === 1 ? parseCnpj : parseCpf}
                      disabled={cannotUpdate || !entityManager.isNew}
                      component={CustomMaskedField}
                      mask={values.tipobloqueio === 1 ? cnpjRegex : cpfRegex}
                    />
                  </Grid>
                )}
                {values.tipobloqueio === 3 && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      name="placa"
                      label="Placa"
                      parse={parsePlaca}
                      disabled={cannotUpdate || !entityManager.isNew}
                      component={CustomTextField}
                    />
                  </Grid>
                )}
                {!initialCodigosMotivos?.length > 0 && !entityManager.isNew && (
                  <Grid item xs={12} sm={12}>
                    <Field
                      fullWidth
                      name="motivo"
                      label="Motivo"
                      disabled
                      component={CustomTextField}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Field
                    name="codigosMotivos"
                    setCodigosMotivos={setCodigosMotivos}
                    showError={showErrorCodigosMotivos}
                    disabled={cannotUpdate}
                    component={AutocompleteCodigosMotivos}
                    options={codigosMotivosOptions}
                  />
                </Grid>
                {entityManager.item.dataDesbloqueio && (
                  <Grid item xs={12} sm={12}>
                    <Field
                      fullWidth
                      name="motivodesbloqueio"
                      label="Motivo do Desbloqueio"
                      disabled={cannotUpdate || !entityManager.isNew}
                      component={CustomTextField}
                    />
                  </Grid>
                )}
                {entityManager.item.usuarioBloqueioId && (
                  <Grid item sm={6} md={6} xs={12}>
                    <Field
                      fullWidth
                      name="usuarioBloqueioId"
                      label="Usuário Bloqueio"
                      disabled
                      component={UsuarioAutocomplete}
                    />
                  </Grid>
                )}
                {entityManager.item.dataDesbloqueio &&
                  entityManager.item.usuarioDesbloqueioId && (
                    <Grid item sm={6} md={6} xs={12}>
                      <Field
                        fullWidth
                        name="usuarioDesbloqueioId"
                        label="Usuário Desbloqueio"
                        disabled
                        component={UsuarioAutocomplete}
                      />
                    </Grid>
                  )}
                <Grid item xs={12}>
                  <Field
                    name="notasinternas"
                    label="Notas Internas"
                    disabled={cannotUpdate}
                    parse={val => (val == null ? '' : val)}
                    fullWidth
                    multiline
                    minRows={5}
                    component={CustomTextField}
                    onChange={event => setNotasInternas(event?.target?.value)}
                    value={notasInternas}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            )
          }}
        </CrudForm>
      )}
      {activeStep === 1 && (
        <Card className={classes.card}>
          <AnexosCard
            match={props.match}
            canAddNew={ability.can('create', 'bloqueio-manual-kmm')}
            cannotUpdate={cannotUpdate}
          />
        </Card>
      )}
      {(activeStep === historicoStepIndex || historicoVisitado) && (
        <Card
          className={classes.card}
          style={{
            display: activeStep === historicoStepIndex ? 'block' : 'none'
          }}
        >
          <HistoricoBloqueioDesbloqueio
            key={historicoRefreshKey}
            repository={bloqueioManualRepository}
            id={entityManager.item.id}
          />
        </Card>
      )}
      <ComplexDialog
        open={!!confirmationDialog.open}
        buttonLabel="Não"
        handleClose={closeDialog}
        primaryAction={handleConfirmationDialog}
        title="Deseja confirmar essa operação?"
        message={confirmationDialog.mensagem}
        primaryActionButtonLabel="Confirmar"
      >
        {!confirmationDialog.isBloqueio && (
          <>
            <Typography gutterBottom>
              Informe o motivo para o desbloqueio
            </Typography>
            <Field
              fullWidth
              multiline
              label="Motivo do Desbloqueio"
              name="motivoDesbloqueio"
              component={CustomTextField}
            />
          </>
        )}
      </ComplexDialog>
    </>
  )
}

export default BloqueioManualForm
